import React, { useState } from 'react';
import { Flame, Home, CheckCircle2 } from 'lucide-react';

interface OvenSelectorProps {
  value: string;
  onChange: (type: string, details?: any) => void;
}

export function OvenSelector({ value, onChange }: OvenSelectorProps) {
  const [showWoodDetails, setShowWoodDetails] = useState(false);
  const [woodDetails, setWoodDetails] = useState({
    size: '',
    lastUsed: ''
  });

  const handleWoodOvenSelect = () => {
    onChange('wood-fired');
    setShowWoodDetails(true);
  };

  const calculateHeatingTime = (size: string, lastUsed: string) => {
    const lastUsedDate = new Date(lastUsed);
    const daysSinceLastUse = Math.floor(
      (new Date().getTime() - lastUsedDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (size === 'large') { // 2m
      return daysSinceLastUse <= 4 ? 3 : 4;
    } else if (size === 'medium') { // 1.2m
      return 2;
    } else {
      return 1.5;
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="font-medium text-white mb-4">Select Oven Type</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => onChange('domestic')}
          className={`p-4 rounded-lg border transition-all duration-200 relative ${
            value === 'domestic' 
              ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10' 
              : 'border-white/10 bg-white/5 hover:bg-white/10'
          }`}
        >
          <Home className={`w-6 h-6 mx-auto mb-2 ${value === 'domestic' ? 'text-amber-300' : 'text-white/70'}`} />
          <span className="block font-medium text-white">Domestic Oven</span>
          {value === 'domestic' && (
            <div className="absolute top-2 right-2">
              <CheckCircle2 className="w-5 h-5 text-amber-400" />
            </div>
          )}
        </button>

        <button
          onClick={handleWoodOvenSelect}
          className={`p-4 rounded-lg border transition-all duration-200 relative ${
            value === 'wood-fired' 
              ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10' 
              : 'border-white/10 bg-white/5 hover:bg-white/10'
          }`}
        >
          <Flame className={`w-6 h-6 mx-auto mb-2 ${value === 'wood-fired' ? 'text-amber-300' : 'text-white/70'}`} />
          <span className="block font-medium text-white">Wood-Fired</span>
          {value === 'wood-fired' && (
            <div className="absolute top-2 right-2">
              <CheckCircle2 className="w-5 h-5 text-amber-400" />
            </div>
          )}
        </button>
      </div>

      {showWoodDetails && (
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/90 mb-1">
              Oven Size
            </label>
            <select
              value={woodDetails.size}
              onChange={(e) => {
                const newDetails = { ...woodDetails, size: e.target.value };
                setWoodDetails(newDetails);
                onChange('wood-fired', newDetails);
              }}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
            >
              <option value="" className="bg-gray-900">Select size...</option>
              <option value="small" className="bg-gray-900">Small (under 1.2m)</option>
              <option value="medium" className="bg-gray-900">Medium (1.2m)</option>
              <option value="large" className="bg-gray-900">Large (2m)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/90 mb-1">
              Last Used
            </label>
            <input
              type="date"
              value={woodDetails.lastUsed}
              onChange={(e) => {
                const newDetails = { ...woodDetails, lastUsed: e.target.value };
                setWoodDetails(newDetails);
                onChange('wood-fired', newDetails);
              }}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
            />
          </div>

          {woodDetails.size && woodDetails.lastUsed && (
            <div className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/10">
              <p className="text-sm text-white/80">
                Estimated heating time: <span className="text-amber-300 font-medium">{calculateHeatingTime(woodDetails.size, woodDetails.lastUsed)} hours</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}